import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Play, Tags } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

/** Pulls the keyword chips back from HelpJuice for imported articles. Fills
 *  blanks only — keywords typed here (or accepted from the AI assistant) win. */
export default function KbKeywordBackfillCard() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/kb/keywords/backfill");
      setSt(data);
      return data;
    } catch { return null; }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!st?.running) return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [st?.running]);

  const run = async () => {
    setBusy(true);
    try {
      await api.post("/kb/keywords/backfill");
      toast.success("Backfill started — it runs in the background");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start the backfill");
    } finally { setBusy(false); }
  };

  const pct = st?.total ? Math.round((100 * (st.progress || 0)) / st.total) : 0;

  return (
    <Card className="px-5 py-4" data-testid="kb-keyword-backfill-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-slate-800 dark:text-slate-100">
            <Tags className="h-4 w-4 text-slate-400" /> Article keywords
          </h3>
          <p className="mt-1 max-w-2xl text-xs text-slate-500 dark:text-slate-400">
            {st
              ? `${(st.with_keywords || 0).toLocaleString()} articles carry keywords, ${(st.without_keywords || 0).toLocaleString()} have none. The HelpJuice export never included them, so they can be pulled back from the live account — blanks only, so anything written here stays.`
              : "Loading…"}
          </p>
        </div>
        <Button size="sm" onClick={run} disabled={busy || st?.running || st?.configured === false}
          data-testid="kb-keyword-backfill-run">
          {busy || st?.running ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            : <Play className="mr-1.5 h-3.5 w-3.5" />}
          {st?.running ? "Running…" : "Fetch keywords"}
        </Button>
      </div>

      {st?.running && (
        <div className="mt-3" data-testid="kb-keyword-backfill-progress">
          <div className="h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
            <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} />
          </div>
          <p className="mt-1.5 text-[11px] text-slate-500 dark:text-slate-400">
            {st.progress || 0} of {st.total || 0} checked · {st.updated || 0} filled in
          </p>
        </div>
      )}
      {!st?.running && st?.finished && (
        <p className="mt-2 text-[11px] text-slate-500 dark:text-slate-400" data-testid="kb-keyword-backfill-result">
          Last run: {st.updated || 0} articles filled with {st.keywords_added || 0} keywords ·
          {" "}{st.no_keywords || 0} had none in HelpJuice
          {st.failed ? ` · ${st.failed} failed` : ""}
          {st.error ? ` · stopped: ${st.error}` : ""}
        </p>
      )}
      {st?.configured === false && (
        <p className="mt-2 text-xs text-amber-700 dark:text-amber-400" data-testid="kb-keyword-backfill-unconfigured">
          HelpJuice isn&apos;t configured in this environment, so there&apos;s nothing to pull from.
        </p>
      )}

      {!!(st?.gaps || []).length && (
        <div className="mt-3 border-t border-slate-100 pt-3 dark:border-slate-800"
          data-testid="kb-keyword-gaps">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            Needs keywords — most read first
          </div>
          <ul className="mt-1.5 space-y-0.5">
            {st.gaps.map((g) => (
              <li key={g.slug} className="flex items-center justify-between gap-3">
                <Link to={`/kb/a/${g.slug}/edit`} data-testid={`kb-keyword-gap-${g.slug}`}
                  className="min-w-0 flex-1 truncate text-xs text-slate-600 no-underline hover:text-primary dark:text-slate-300">
                  {g.name}
                </Link>
                <span className="shrink-0 text-[11px] text-slate-400">{(g.views || 0).toLocaleString()} views</span>
              </li>
            ))}
          </ul>
          <Link to="/kb/suggestions" data-testid="kb-keyword-gaps-suggestions"
            className="mt-2 inline-block text-[11px] font-medium text-primary no-underline hover:underline">
            Or take the words readers actually search for →
          </Link>
        </div>
      )}
    </Card>
  );
}
